import React, { useEffect, useRef, useState } from 'react';
import { host } from '../script/variables';

/* ─── Status meta ────────────────────────────────────────────
   icon  : emoji shown in the badge
   label : human-readable status name shown next to the icon
   ─────────────────────────────────────────────────────────── */
const STATUS_META = {
  Pending:   { color: '#f59e0b', bg: '#fef3c7', icon: '⏳', label: 'Pending'   },
  Accepted:  { color: '#10b981', bg: '#d1fae5', icon: '✓',  label: 'Accepted'  },
  Rejected:  { color: '#ef4444', bg: '#fee2e2', icon: '✕',  label: 'Rejected'  },
  Preparing: { color: '#3b82f6', bg: '#dbeafe', icon: '👨‍🍳', label: 'Preparing' },
  Ready:     { color: '#10b981', bg: '#d1fae5', icon: '✅', label: 'Ready'     },
  Hold:      { color: '#8b5cf6', bg: '#ede9fe', icon: '⏸',  label: 'On Hold'   },
  Cancelled: { color: '#ef4444', bg: '#fee2e2', icon: '✕',  label: 'Cancelled' },
  Completed: { color: '#6366f1', bg: '#ede9fe', icon: '💜', label: 'Completed' },
  Due:       { color: '#f97316', bg: '#ffedd5', icon: '⏸',  label: 'Due'       },
};

/* ─── Status progression ─────────────────────────────────────
   Each active status maps to:
     next     – the status to advance to
     label    – CTA button text
     btnClass – visual style
   null = terminal state (no advance button)
   ─────────────────────────────────────────────────────────── */
const NEXT_ACTION = {
  Accepted:  { next: 'Preparing', label: 'Start Preparing', btnClass: 'ops-btn-primary'  },
  Preparing: { next: 'Ready',     label: 'Mark as Ready',   btnClass: 'ops-btn-success'  },
  Ready:     { next: 'Completed', label: 'Mark Completed',  btnClass: 'ops-btn-accent'   },
  Completed: null,
  Cancelled: null,
  Rejected:  null,
  // Pending has no advance button here — accept/reject is handled by AcceptRejectBar
  Pending:   null,
};

/* Progress steps shown in the dot indicator */
const PROGRESS_STEPS = ['Accepted', 'Preparing', 'Ready', 'Completed'];

const OrderPaymentStatusPartner = ({ orderid, cafeid, setChangeStatus, changeStatus, socket }) => {
  const [paymentStatus,  setPaymentStatus]  = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState('Pending');
  const [updating,       setUpdating]       = useState(false);
  const [showCancel,     setShowCancel]     = useState(false);
  const intervalRef = useRef(null);

  /* ── Fetch status ─────────────────────────────────────── */
  const fetchStatus = async () => {
    if (!orderid) return;
    try {
      const token = localStorage.getItem('partnertoken');
      const res = await fetch(`${host}/partner/orderstatus`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body:    JSON.stringify({ orderid }),
      });
      if (res.ok) {
        const data = await res.json();
        setPaymentStatus(data.paymentStatus  || '');
        setDeliveryStatus(data.deliveryStatus || 'Pending');
        // Stop polling once the order is fully terminal
        if (
          ['Completed', 'Cancelled', 'Rejected'].includes(data.deliveryStatus)
        ) {
          clearInterval(intervalRef.current);
        }
      }
    } catch (err) {
      console.error('OrderPaymentStatusPartner fetch:', err);
    }
  };

  /* ── Update delivery status ───────────────────────────── */
  const updateDeliveryStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('partnertoken');
      const res = await fetch(`${host}/order/updateDeliveryStatus`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body:    JSON.stringify({ orderid, status: newStatus, cafeid }),
      });
      if (res.ok) {
        setDeliveryStatus(newStatus);
        setShowCancel(false);
        socket.emit('order-deliveryStatus-change', { status: newStatus, cafeid, orderid });
        if (newStatus === 'Completed' || newStatus === 'Cancelled') {
          setChangeStatus(p => !p);
        }
      }
    } catch (err) {
      console.error('OrderPaymentStatusPartner update:', err);
    } finally {
      setUpdating(false);
    }
  };

  /* ── Effects ──────────────────────────────────────────── */
  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 5000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderid]);

  useEffect(() => {
    socket.emit('join-room', orderid);
    socket.on('connect',  () => console.log('connected', socket.id));
    socket.on('welcome',  s  => console.log(s));
    return () => { socket.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Derived ──────────────────────────────────────────── */
  const dlvMeta    = STATUS_META[deliveryStatus] ?? STATUS_META.Pending;
  const nextAction = NEXT_ACTION[deliveryStatus] ?? null;
  const isDone     = ['Completed', 'Cancelled', 'Rejected'].includes(deliveryStatus);
  const isPending  = deliveryStatus === 'Pending';

  // Dot index helpers
  const stepIndex = (s) => PROGRESS_STEPS.indexOf(s);
  const currentIdx = stepIndex(deliveryStatus);

  return (
    <div className="ops-root">

      {/* ── Current status badge + progress dots ── */}
      <div className="ops-status-row">
        <span
          className="ops-current-badge"
          style={{
            color:       dlvMeta.color,
            background:  dlvMeta.bg,
            borderColor: dlvMeta.color + '40',
          }}
        >
          {dlvMeta.icon} {dlvMeta.label}
        </span>

        {/* Progress dots — only for the active pipeline */}
        {!isDone && !isPending && (
          <div className="ops-progress-dots">
            {PROGRESS_STEPS.map((s, idx) => (
              <span
                key={s}
                className={[
                  'ops-dot',
                  deliveryStatus === s   ? ' active' : '',
                  idx < currentIdx       ? ' done'   : '',
                ].join('')}
                style={deliveryStatus === s ? { background: dlvMeta.color } : {}}
                title={s}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Pending: no actions here (AcceptRejectBar handles it) ── */}
      {isPending && (
        <p className="ops-hint">
          ⏳ Awaiting acceptance — use Accept / Reject above
        </p>
      )}

      {/* ── Payment hint ── */}
      {!isPending && paymentStatus === 'Pending' && (
        <p className="ops-hint">💡 Collect payment at counter to complete</p>
      )}

      {/* ── Action buttons — only for non-terminal, non-pending orders ── */}
      {!isDone && !isPending && (
        <div className="ops-actions">

          {/* Primary: advance to next status */}
          {nextAction && (
            <button
              className={`ops-btn ${nextAction.btnClass}`}
              onClick={() => updateDeliveryStatus(nextAction.next)}
              disabled={updating}
            >
              {updating ? <span className="ops-spinner-inline" /> : null}
              {nextAction.label}
            </button>
          )}

          {/* Cancel toggle */}
          {!showCancel ? (
            <button
              className="ops-btn ops-btn-ghost"
              onClick={() => setShowCancel(true)}
              disabled={updating}
            >
              Cancel Order
            </button>
          ) : (
            <div className="ops-cancel-confirm">
              <span>Sure?</span>
              <button
                className="ops-btn ops-btn-danger"
                onClick={() => updateDeliveryStatus('Cancelled')}
                disabled={updating}
              >
                Yes, Cancel
              </button>
              <button
                className="ops-btn ops-btn-ghost"
                onClick={() => setShowCancel(false)}
              >
                No
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderPaymentStatusPartner;